import { combineLatest, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, switchMap, take } from 'rxjs/operators';

import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Params } from '@angular/router';
import { FilterService } from '@core/services/filter/filter.service';
import { customFilterParams } from '@features/product-filter/product-filter.config';
import { StoreStockContainerComponent } from '@features/store-stock/store-stock-container/store-stock-container.component';
import { StoresService } from '@features/stores-overview/service/stores.service';
import { OverlayService } from '@libs/overlay';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { StoreViewModel } from '@models/api.models';
import isEqual from 'lodash.isequal';
import { FindStoreFromId } from '../../reserve-in-store/reserve-in-store-pipes/find-store-from-id.pipe';
import { CanChangeStoreInReservationPipe } from '../../reserve-in-store/reserve-in-store-pipes/can-change-store-in-reservation.pipe';
import { TranslateSafePipe } from '../../../@shared/translate-safe/translate-safe.pipe';
import { SvgIconComponent } from '../../../@shared/svg-icon/svg-icon.component';
import { NgIf, AsyncPipe } from '@angular/common';
import { LoadingDotsComponent } from '../../../@shared/loading-dots/loading-dots.component';

@UntilDestroy()
@Component({
    selector: 'app-store-stock-facet',
    templateUrl: './store-stock-facet.component.html',
    styleUrls: ['./store-stock-facet.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingDotsComponent, NgIf, FormsModule, ReactiveFormsModule, SvgIconComponent, AsyncPipe, TranslateSafePipe, CanChangeStoreInReservationPipe, FindStoreFromId]
})
export class StoreStockFacetComponent implements OnInit, OnChanges {
    private stockOverlayHasBeenUsed: boolean = false;
    private filteredStoreRs$: ReplaySubject<StoreViewModel | null> = new ReplaySubject<StoreViewModel | null>();
    public store$ = this.filteredStoreRs$.asObservable();
    public checkbox: UntypedFormControl = new UntypedFormControl(false);
    public storeIdParamKey = customFilterParams.storeId;

    @Input() disabled: boolean = false;
    @Input() storeId?: number;

    @Output()
    public readonly filterByStoreChange: EventEmitter<Params> = new EventEmitter();

    constructor(
        public readonly overlayService: OverlayService,
        public readonly storesService: StoresService,
        public readonly filterService: FilterService
    ) {}

    ngOnInit(): void {
        // If favorite store changes, set checkbox to true, and start filtering.
        this.storesService.favoriteStore$.pipe(untilDestroyed(this)).subscribe(store => {
            this.filteredStoreRs$.next(store);
            if (this.stockOverlayHasBeenUsed) {
                this.checkbox.setValue(true);
            }
        });

        // Check if url has storeId in it. Also set checkbox value to checked if storeId exists.
        // It's important that we do this after favorite store has been set, so we can overwrite it if another store exists in the url
        this.filterService.queryParams$
            .pipe(
                take(1),
                switchMap(params => {
                    const storeStockId = this.storeId || params[customFilterParams.storeId];
                    return this.storesService.getStoreById(storeStockId);
                }),
                untilDestroyed(this)
            )
            .subscribe(store => {
                if (store) {
                    this.filteredStoreRs$.next(store);
                }

                this.checkbox.setValue(!!store?.storeId);
            });

        combineLatest([this.checkbox.valueChanges.pipe(untilDestroyed(this)), this.store$.pipe(untilDestroyed(this))])
            .pipe(
                distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
                untilDestroyed(this)
            )
            .subscribe(([isChecked, store]) => {
                const { storeId, postalCode } = store || {};
                if (isChecked && !storeId) {
                    this.checkbox.setValue(false);
                    this.openStockOverlay();
                    return;
                }

                this.filterService.trackingStockStoreEvents(isChecked ? postalCode : undefined);

                if (storeId) {
                    this.filterByStoreChange.emit({
                        [customFilterParams.storeId]: isChecked ? storeId : null,
                        [customFilterParams.stockQuantity]: isChecked ? '1|*' : null // '1|*' means 1 or more
                    });
                }
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const { disabled } = changes;

        if (disabled) {
            const { currentValue } = disabled;
            if (currentValue) {
                this.checkbox.disable();
            } else {
                this.checkbox.enable();
            }

            if (currentValue && this.checkbox.value) {
                this.checkbox.setValue(false);
            }
        }
    }

    public openStockOverlay(): void {
        this.stockOverlayHasBeenUsed = true;
        this.overlayService.open(StoreStockContainerComponent, { data: { stockSidebarType: 'select' } });
    }
}
